import type { FloorplanDocumentV2 } from "./floorplan-document.ts";
import { furnitureCatalogItem } from "./furniture-catalog.ts";

type ObjectValue = Record<string, unknown>;
function requireValue(condition: unknown): asserts condition {
  if (!condition) throw new Error("The project contains invalid or incomplete data.");
}
function object(value: unknown): ObjectValue {
  requireValue(value && typeof value === "object" && !Array.isArray(value));
  return value as ObjectValue;
}
function text(value: unknown, max = 500): value is string { return typeof value === "string" && value.length > 0 && value.length <= max; }
function number(value: unknown, min = -1_000_000, max = 1_000_000): value is number { return typeof value === "number" && Number.isFinite(value) && value >= min && value <= max; }
function list(value: unknown, max = 5000): unknown[] { requireValue(Array.isArray(value) && value.length <= max); return value; }
function point(value: unknown) { requireValue(Array.isArray(value) && value.length === 2 && value.every(v => number(v))); }
function box(value: unknown) { const b = object(value); requireValue(number(b.x) && number(b.y) && number(b.width, 0.000001) && number(b.height, 0.000001)); }
function uniqueIds(values: unknown[]) { const ids = values.map(v => object(v).id); requireValue(ids.every(id => text(id, 200)) && new Set(ids).size === ids.length); }
function confidence(value: unknown) { requireValue(number(value, 0, 1)); }
const fixtureKinds = new Set(["fridge", "stove", "sink", "island", "cupboard", "toilet", "shower", "bathtub", "washer", "countertop"]);

/** Shared browser/Worker boundary. Missing legacy collections are normalised;
 * malformed supplied data is rejected, never silently discarded. */
export function parseValidatedProject(value: unknown): FloorplanDocumentV2 {
  let nodes = 0;
  const checkTree = (v: unknown, depth: number) => {
    requireValue(depth <= 32 && ++nodes <= 200000);
    if (typeof v === "number") requireValue(Number.isFinite(v));
    else if (typeof v === "string") requireValue(v.length <= 32 * 1024 * 1024);
    else if (v && typeof v === "object") Object.values(v).forEach(child => checkTree(child, depth + 1));
    else requireValue(v === null || v === undefined || typeof v === "boolean");
  };
  checkTree(value, 0);
  const d = object(value);
  requireValue(d.schemaVersion === 2 && text(d.id, 200) && text(d.name) && text(d.createdAt) && text(d.updatedAt));
  const source = object(d.source), model = object(d.model);
  requireValue(text(source.name) && text(source.mimeType) && number(source.width, 1, 100000) && number(source.height, 1, 100000));
  requireValue(source.previewDataUrl === undefined || typeof source.previewDataUrl === "string");
  requireValue(text(model.version) && ["webgpu", "wasm", "geometry-fallback"].includes(String(model.runtime)));
  const levels = list(d.levels, 30); requireValue(levels.length); uniqueIds(levels);
  const normalizedLevels = levels.map(value => {
    const l = object(value), s = object(l.structure), r = object(l.sourceRegion);
    requireValue(text(l.name) && number(l.order, 0, 100) && Number.isInteger(l.order) && number(l.elevation, -100, 1000));
    confidence(l.confidence); requireValue(typeof l.confirmed === "boolean");
    requireValue(list(l.provenance, 20).every(p => ["semantic-model", "geometry", "ocr", "topology", "user"].includes(String(p))));
    box(r); requireValue(text(r.id) && text(r.name)); confidence(r.confidence);
    box(s.footprint); requireValue(text(s.regionId) && number(s.sourceWidth, 1, 100000) && number(s.sourceHeight, 1, 100000));
    confidence(s.confidence); requireValue(number(s.roomCount, 0, 5000));
    const diagnostics = object(s.diagnostics);
    for (const field of ["threshold", "wallThickness", "geometryVotes", "topologyVotes", "openingVotes", "stairVotes"]) requireValue(number(diagnostics[field], 0));
    if (s.rotationCenter !== undefined) point(s.rotationCenter);
    if (s.sourceRotationDegrees !== undefined) requireValue(number(s.sourceRotationDegrees, -360, 360));
    requireValue(s.floorTextureUrl === undefined || typeof s.floorTextureUrl === "string");
    const walls = list(s.walls); uniqueIds(walls);
    for (const value of walls) {
      const w = object(value); point(w.start); point(w.end); confidence(w.confidence);
      requireValue(["horizontal", "vertical"].includes(String(w.axis)) && number(w.thickness, 0.000001));
      requireValue(w.weight === undefined || ["heavy", "light"].includes(String(w.weight)));
      for (const value of list(w.openings, 200)) {
        const o = object(value); requireValue(["door", "window"].includes(String(o.kind)) && number(o.offset, 0) && number(o.width, 0.000001)); confidence(o.confidence);
      }
      if (w.railSpans !== undefined) for (const span of list(w.railSpans, 100)) point(span);
    }
    const stairs = list(s.stairs, 100); uniqueIds(stairs);
    for (const value of stairs) {
      const t = object(value); box(t); confidence(t.confidence);
      requireValue(["horizontal", "vertical"].includes(String(t.runAxis)) && number(t.stepCount, 1, 200));
      requireValue(t.ascend === undefined || ["start", "end"].includes(String(t.ascend)));
      if (t.winder !== undefined) box(t.winder);
      if (t.flight !== undefined) box(t.flight);
    }
    const fixtures = list(s.fixtures ?? []); uniqueIds(fixtures);
    for (const value of fixtures) {
      const f = object(value); box(f); confidence(f.confidence); requireValue(fixtureKinds.has(String(f.kind)) && number(f.rotation, -10000, 10000));
      requireValue(f.front === undefined || ["north", "south", "east", "west"].includes(String(f.front)));
    }
    const rooms = list(s.rooms ?? []); uniqueIds(rooms);
    for (const value of rooms) { const room = object(value); const polygon = list(room.polygon, 1000); requireValue(polygon.length >= 3); polygon.forEach(point); requireValue(number(room.areaPx, 0, 1e12)); confidence(room.confidence); }
    for (const value of list(s.outdoorAreas, 100)) { const area = object(value); box(area); requireValue(text(area.id) && ["top", "right", "bottom", "left"].includes(String(area.side))); confidence(area.confidence); }
    return { ...l, structure: { ...s, rooms, fixtures } };
  });
  const scale = object(d.scale ?? { metresPerPixel: 0, source: "provisional", confidence: 0 });
  requireValue(number(scale.metresPerPixel, 0, 100) && ["user", "door-width", "provisional"].includes(String(scale.source))); confidence(scale.confidence);
  for (const value of list(d.issues)) { const issue = object(value); requireValue(text(issue.id) && text(issue.message, 5000) && typeof issue.resolved === "boolean" && ["info", "warning", "blocking"].includes(String(issue.severity))); }
  for (const value of list(d.edits, 10000)) { const edit = object(value); requireValue(text(edit.id) && text(edit.kind) && text(edit.createdAt) && text(edit.levelId)); }
  const furnishings = list(d.furnishings ?? []); uniqueIds(furnishings);
  const levelIds = new Set(levels.map(l => object(l).id));
  for (const value of furnishings) {
    const f = object(value);
    requireValue(text(f.catalogId) && furnitureCatalogItem(f.catalogId) && levelIds.has(f.levelId));
    requireValue(number(f.x, -10000, 10000) && number(f.z, -10000, 10000) && number(f.rotation, -10000, 10000));
    requireValue(f.mirrored === undefined || typeof f.mirrored === "boolean");
  }
  return { ...d, levels: normalizedLevels, scale, furnishings } as FloorplanDocumentV2;
}

export function isValidProject(value: unknown): value is FloorplanDocumentV2 {
  try { parseValidatedProject(value); return true; } catch { return false; }
}
